import HistoryIcon from '@mui/icons-material/History';
import RefreshIcon from '@mui/icons-material/Refresh';
import ViewSidebarIcon from '@mui/icons-material/ViewSidebar';
import {
  Box,
  Button,
  Divider,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CanvasHierarchyPanel from './CanvasHierarchyPanel';
import type { UiTab } from '../types/uiSchema';
import { syncTabsFromFormSchema } from '../utils/canvasUiSchema';
import { STUDIO_PANEL } from './studioDesign';

type ConfigRow = { id: string; name: string };

export default function AdvancedInspectorPanel(props: {
  activeTenantId: string | null;
  configs: ConfigRow[];
  selectedConfigId: string;
  onSelectConfigId: (id: string) => void;

  formSchema: Record<string, unknown> | null;
  selectedSectionIndex: number;
  onSelectSectionIndex: (idx: number) => void;

  autoLayoutMode: 'rules' | 'ai' | 'hybrid';
  onChangeAutoLayoutMode: (m: 'rules' | 'ai' | 'hybrid') => void;
  isAutoLayouting: boolean;
  onRunAutoLayout: () => void;
  onOpenAutoLayoutHistory: () => void;
  onReloadFromServer: () => void;

  onOpenAddSection: () => void;
  onOpenAddField: () => void;
  onOpenDeleteSection: () => void;
  canDeleteSection: boolean;

  canvasTabs: UiTab[];
  onChangeTabs: (tabs: UiTab[]) => void;
  onInfo: (msg: string) => void;

  autoLayoutSource: string;
  autoLayoutDecisions: string[];
  autoLayoutWarnings: string[];
}) {
  const {
    activeTenantId,
    configs,
    selectedConfigId,
    onSelectConfigId,
    formSchema,
    selectedSectionIndex,
    onSelectSectionIndex,
    autoLayoutMode,
    onChangeAutoLayoutMode,
    isAutoLayouting,
    onRunAutoLayout,
    onOpenAutoLayoutHistory,
    onReloadFromServer,
    onOpenAddSection,
    onOpenAddField,
    onOpenDeleteSection,
    canDeleteSection,
    canvasTabs,
    onChangeTabs,
    onInfo,
    autoLayoutSource,
    autoLayoutDecisions,
    autoLayoutWarnings,
  } = props;

  return (
    <Box
      sx={{
        width: { xs: '100%', lg: STUDIO_PANEL.leftWidth },
        flexShrink: 0,
        borderRight: { lg: 1 },
        borderBottom: { xs: 1, lg: 0 },
        borderColor: 'divider',
        bgcolor: (t) => alpha(t.palette.primary.main, 0.03),
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <ViewSidebarIcon fontSize="small" color="primary" />
        <Typography variant="subtitle2" fontWeight={700} color="primary">
          Inspector
        </Typography>
      </Stack>
      <Stack spacing={1.75} sx={{ flex: 1, minHeight: 0, overflow: 'auto', pr: 0.5 }}>
        <TextField
          select
          size="small"
          label="Config"
          fullWidth
          value={selectedConfigId}
          onChange={(e) => onSelectConfigId(String(e.target.value || ''))}
          disabled={!activeTenantId}
        >
          {configs.map((c) => (
            <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Section (active frame)"
          fullWidth
          value={selectedSectionIndex}
          onChange={(e) => onSelectSectionIndex(Number(e.target.value))}
          disabled={!formSchema}
        >
          {(((formSchema?.sections as Array<Record<string, unknown>> | undefined) || [])).map((sec, idx) => (
            <MenuItem key={String(sec.id ?? idx)} value={idx}>
              {String((sec as { title?: string }).title ?? (sec as { name?: string }).name ?? sec.id ?? `Section ${idx + 1}`)}
            </MenuItem>
          ))}
        </TextField>
        <Divider />
        <Typography variant="caption" color="text.secondary">
          Structure
        </Typography>
        <TextField
          select
          size="small"
          label="Auto-layout mode"
          fullWidth
          value={autoLayoutMode}
          onChange={(e) => onChangeAutoLayoutMode(e.target.value as 'rules' | 'ai' | 'hybrid')}
          disabled={!selectedConfigId || isAutoLayouting}
        >
          <MenuItem value="rules">Rules</MenuItem>
          <MenuItem value="ai">AI</MenuItem>
          <MenuItem value="hybrid">Hybrid</MenuItem>
        </TextField>
        <Button
          variant="contained"
          size="small"
          fullWidth
          onClick={onRunAutoLayout}
          disabled={!selectedConfigId || isAutoLayouting}
        >
          {isAutoLayouting ? 'Generating…' : 'Auto-layout'}
        </Button>
        <Button
          startIcon={<HistoryIcon />}
          variant="outlined"
          size="small"
          fullWidth
          onClick={onOpenAutoLayoutHistory}
          disabled={!selectedConfigId}
        >
          Auto-layout history
        </Button>
        <Button
          startIcon={<RefreshIcon />}
          variant="outlined"
          size="small"
          fullWidth
          onClick={onReloadFromServer}
          disabled={!selectedConfigId}
        >
          Reload from server
        </Button>
        <Button variant="outlined" size="small" fullWidth onClick={onOpenAddSection} disabled={!formSchema}>
          Add section
        </Button>
        <Button variant="outlined" size="small" fullWidth onClick={onOpenAddField} disabled={!formSchema}>
          Add field
        </Button>
        <Button
          variant="outlined"
          color="error"
          size="small"
          fullWidth
          onClick={onOpenDeleteSection}
          disabled={!formSchema || !canDeleteSection}
        >
          Delete section…
        </Button>
        <Divider sx={{ my: 0.5 }} />
        <CanvasHierarchyPanel
          tabs={canvasTabs}
          onChangeTabs={onChangeTabs}
          onSyncFromFormSchema={() => {
            if (!formSchema) return;
            onChangeTabs(syncTabsFromFormSchema(formSchema));
            onInfo('Tabs synced from form schema (save to persist).');
          }}
          disabled={!formSchema}
        />
        {(autoLayoutSource || autoLayoutDecisions.length > 0 || autoLayoutWarnings.length > 0) && (
          <>
            <Divider sx={{ my: 0.5 }} />
            <Typography variant="caption" color="text.secondary">
              Explainability
            </Typography>
            {autoLayoutSource ? (
              <Typography variant="caption" color="text.secondary">
                Source: <b>{autoLayoutSource}</b>
              </Typography>
            ) : null}
            {autoLayoutDecisions.slice(0, 4).map((d, idx) => (
              <Typography key={`decision-${idx}`} variant="caption" color="text.secondary">
                - {d}
              </Typography>
            ))}
            {autoLayoutWarnings.slice(0, 4).map((w, idx) => (
              <Typography key={`warn-${idx}`} variant="caption" color="warning.main">
                ! {w}
              </Typography>
            ))}
          </>
        )}
      </Stack>
    </Box>
  );
}

